import { useShallow } from 'zustand/react/shallow'

import { AstraTooltip, astraTwMerge } from '~/astra'
import { selectLocationsDisplayMode } from '~/store/slices/surgeonSchedulerSlice'
import { useStore } from '~/store/store'
import {
    Location,
    Practitioner,
    PRACTITIONER_CELL_LOCATION_INCLUDE,
    useLocations,
    usePractitionerSchedule,
    usePractitionerScheduleStatusesForCell,
} from '~/storm'

import { PractitionerActivityDnd } from '../../PractitionerActivityDnd'
import { commentStyles, getEditInfoText, usePractitionerCellErrors } from '../../shared'
import { Locations, useCellLocationSchedules } from './Schedules/Locations'
import { Statuses } from './Schedules/Statuses'
import { useActivityDrop } from './Schedules/useActivityDrop'

type Props = {
    practitionerId: Practitioner['id']
    date: string
}

export const PractitionerCell = ({ practitionerId, date }: Props) => {
    const { locationsDisplayMode, showPreview } = useStore(
        useShallow(state => ({
            showPreview: state.surgeonScheduler.showRecurringAssignmentApplyPreview,
            locationsDisplayMode: selectLocationsDisplayMode(state),
        }))
    )
    const practitionerSchedule = usePractitionerSchedule({
        where: {
            practitioner_id: practitionerId,
            scheduled_date: date,
        },
    })
    const statuses = usePractitionerScheduleStatusesForCell(practitionerId, date)
    const locationSchedules = useCellLocationSchedules(practitionerId, date)
    const cellLocations = useLocations({
        where: { room_code: { in: locationSchedules.allRoomCodes } },
        include: PRACTITIONER_CELL_LOCATION_INCLUDE,
    })

    const gridRows = locationsDisplayMode === 'separate' ? 'grid-rows-2' : ''
    const unavailableStatus = statuses.find(status => status.status_code === 'UNAVAILABLE')
    const commentClassName = practitionerSchedule?.comment ? commentStyles : ''
    const unavailableClassName = unavailableStatus ? 'bg-[#F0F0F3]' : ''
    const { dropProps, toBeDroppedLocation: toBeDroppedLocationSchedule, toBeDroppedStatus } = useActivityDrop({ practitionerId, date })

    const toBeDroppedLocations = toBeDroppedLocationSchedule?.location ? [toBeDroppedLocationSchedule.location] : toBeDroppedStatus?.locations
    const cellErrors = usePractitionerCellErrors({ practitioner_id: practitionerId, scheduled_date: date }, cellLocations)

    if (unavailableStatus) {
        return (
            <AstraTooltip tooltipContent={getEditInfoText(unavailableStatus)}>
                <div className={astraTwMerge('flex h-full w-full items-center justify-center', commentClassName, unavailableClassName)}>
                    <div className="w-1/4 rounded-md border border-strong" />
                </div>
            </AstraTooltip>
        )
    }

    return (
        <PractitionerActivityDnd.Droppable {...dropProps}>
            <div className={astraTwMerge('cell-wrapper grid h-full w-full grid-cols-1 gap-y-1 p-1', gridRows, commentClassName)}>
                <Statuses
                    practitionerId={practitionerId}
                    date={date}
                    toBeDropped={toBeDroppedStatus}
                    cellLocations={cellLocations}
                    showLocationBadges={locationsDisplayMode === 'combine'}
                    statuses={statuses}
                    showPreview={showPreview}
                    // show errors for statuses if locations are not shown separately
                    // done to be able to see location errors when locations are hidden
                    cellErrors={locationsDisplayMode !== 'separate' ? cellErrors : undefined}
                />
                {locationsDisplayMode === 'separate' && (
                    <Locations toBeDropped={toBeDroppedLocations} cellErrors={cellErrors} locationSchedules={locationSchedules} showPreview={showPreview} />
                )}
            </div>
        </PractitionerActivityDnd.Droppable>
    )
}
